import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Users,
  UserCheck,
  Stethoscope,
  CreditCard,
  ShieldCheck,
  ShieldOff,
  Download,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
} from "lucide-react";
import { useAdminApi } from "../../hooks/useAdminApi";

const PAGE_SIZE = 10;

const StatCard = ({ title, value, icon: Icon, color = "orange", loading, onClick }) => (
  <Card
    className={onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}
    onClick={onClick}
  >
    <CardContent className="p-6">
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="h-8 bg-gray-200 rounded w-1/2" />
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value ?? "—"}</p>
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

const Pagination = ({ page, totalPages, onPrev, onNext }) => (
  <div className="flex items-center justify-end gap-3 mt-4">
    <span className="text-sm text-gray-500">
      Page {page + 1} of {Math.max(totalPages, 1)}
    </span>
    <Button variant="outline" size="sm" onClick={onPrev} disabled={page === 0}>
      <ChevronLeft className="w-4 h-4" />
    </Button>
    <Button variant="outline" size="sm" onClick={onNext} disabled={page + 1 >= totalPages}>
      <ChevronRight className="w-4 h-4" />
    </Button>
  </div>
);

const exportCsv = (filename, headers, rows) => {
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const AdminDashboard = () => {
  const { getStats } = useAdminApi();
  const { getSubscriptions } = useAdminApi();
  const { getUsers } = useAdminApi();

  const usersRef = useRef(null);
  const subsRef = useRef(null);

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Users
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userSearchInput, setUserSearchInput] = useState("");
  const [userSearch, setUserSearch] = useState("");

  // Debounce search input → reset to page 0 on change
  useEffect(() => {
    const t = setTimeout(() => {
      setUserSearch(userSearchInput.trim());
      setUsersPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [userSearchInput]);

  // Subscriptions
  const [subscriptions, setSubscriptions] = useState([]);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsPage, setSubsPage] = useState(0);
  const [activeOnly, setActiveOnly] = useState(true);
  const [subsLoading, setSubsLoading] = useState(true);

  // Load stats once
  useEffect(() => {
    getStats()
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []); // eslint-disable-line

  // Load users on page/search change
  useEffect(() => {
    setUsersLoading(true);
    const params = { skip: usersPage * PAGE_SIZE, limit: PAGE_SIZE };
    if (userSearch) params.q = userSearch;
    getUsers(params)
      .then(({ data }) => {
        setUsers(Array.isArray(data?.users) ? data.users : []);
        setUsersTotal(data?.total ?? 0);
      })
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, [usersPage, userSearch]); // eslint-disable-line

  // Load subscriptions on page/filter change
  useEffect(() => {
    setSubsLoading(true);
    const params = { skip: subsPage * PAGE_SIZE, limit: PAGE_SIZE };
    if (activeOnly) params.is_active = true;
    getSubscriptions(params)
      .then(({ data }) => {
        setSubscriptions(Array.isArray(data?.subscriptions) ? data.subscriptions : []);
        setSubsTotal(data?.total ?? 0);
      })
      .catch(() => {})
      .finally(() => setSubsLoading(false));
  }, [subsPage, activeOnly]); // eslint-disable-line

  const scrollTo = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleExportUsers = async () => {
    try {
      const params = { skip: 0, limit: 500 };
      if (userSearch) params.q = userSearch;
      const { data } = await getUsers(params);
      const all = Array.isArray(data?.users) ? data.users : [];
      exportCsv(
        "users.csv",
        ["ID", "Name", "Phone", "Roles", "Verified", "Joined"],
        all.map((u) => [
          u.id,
          u.name,
          u.phone,
          (u.roles || []).join("; "),
          u.is_verified ? "Yes" : "No",
          u.joined ? new Date(u.joined).toLocaleDateString() : "",
        ])
      );
    } catch {}
  };

  const handleExportSubscriptions = async () => {
    try {
      const params = { skip: 0, limit: 500 };
      if (activeOnly) params.is_active = true;
      const { data } = await getSubscriptions(params);
      const all = Array.isArray(data?.subscriptions) ? data.subscriptions : [];
      exportCsv(
        "subscriptions.csv",
        ["Subscription ID", "Name", "Phone", "Plan", "Discount %", "Expires", "Status", "Grandfathered"],
        all.map((s) => [
          s.subscription_id,
          s.name,
          s.phone,
          s.plan,
          s.discount_percent,
          s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "Forever",
          s.is_active ? "Active" : "Expired",
          s.is_grandfathered ? "Yes" : "No",
        ])
      );
    } catch {}
  };

  const usersTotalPages = Math.ceil(usersTotal / PAGE_SIZE);
  const subsTotalPages = Math.ceil(subsTotal / PAGE_SIZE);

  return (
    <div className="space-y-8">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Enrolled Today" value={stats?.enrolled_today} icon={UserCheck} color="green" loading={statsLoading} />
        <StatCard
          title="Total Users"
          value={stats?.total_users?.toLocaleString()}
          icon={Users}
          color="blue"
          loading={statsLoading}
          onClick={() => scrollTo(usersRef)}
        />
        <StatCard title="Total Doctors" value={stats?.total_doctors} icon={Stethoscope} color="orange" loading={statsLoading} />
        <StatCard
          title="Active Subscribers"
          value={stats?.active_subscriptions}
          icon={CreditCard}
          color="emerald"
          loading={statsLoading}
          onClick={() => {
            setActiveOnly(true);
            setSubsPage(0);
            scrollTo(subsRef);
          }}
        />
      </div>

      {/* Users table */}
      <Card ref={usersRef}>
        <CardHeader className="pb-3 space-y-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              All Users{" "}
              {!usersLoading && usersTotal > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">({usersTotal})</span>
              )}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={handleExportUsers} disabled={usersLoading || !users.length}>
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={userSearchInput}
              onChange={(e) => setUserSearchInput(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            {userSearchInput && (
              <button
                type="button"
                onClick={() => setUserSearchInput("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse h-10 bg-gray-100 rounded" />
              ))}
            </div>
          ) : !users.length ? (
            <p className="text-sm text-gray-500 py-4 text-center">No users found.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Roles</TableHead>
                      <TableHead>Verified</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.name || "—"}</TableCell>
                        <TableCell className="text-gray-500 text-sm">{u.phone || "—"}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(u.roles || []).length ? u.roles.map((r) => (
                              <Badge key={r} variant="outline" className="text-xs border-orange-200 text-orange-700">{r}</Badge>
                            )) : <span className="text-gray-400 text-xs">—</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {u.is_verified
                            ? <ShieldCheck className="w-4 h-4 text-green-600" />
                            : <ShieldOff className="w-4 h-4 text-gray-400" />}
                        </TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {u.joined ? new Date(u.joined).toLocaleDateString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Pagination
                page={usersPage}
                totalPages={usersTotalPages}
                onPrev={() => setUsersPage((p) => p - 1)}
                onNext={() => setUsersPage((p) => p + 1)}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Subscription table */}
      <Card ref={subsRef}>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">
              User Subscriptions{" "}
              {!subsLoading && subsTotal > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">({subsTotal})</span>
              )}
            </CardTitle>
            <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
              <button
                className={`px-3 py-1.5 font-medium transition-colors ${activeOnly ? "bg-orange-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                onClick={() => { setActiveOnly(true); setSubsPage(0); }}
              >
                Active
              </button>
              <button
                className={`px-3 py-1.5 font-medium transition-colors ${!activeOnly ? "bg-orange-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                onClick={() => { setActiveOnly(false); setSubsPage(0); }}
              >
                All
              </button>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleExportSubscriptions} disabled={subsLoading || !subscriptions.length}>
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          {subsLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse h-10 bg-gray-100 rounded" />
              ))}
            </div>
          ) : !subscriptions.length ? (
            <p className="text-sm text-gray-500 py-4 text-center">No subscription data available.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Subscription ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Discount</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((sub) => (
                      <TableRow key={sub.subscription_id}>
                        <TableCell className="text-gray-500 text-sm font-mono">{sub.subscription_id || "—"}</TableCell>
                        <TableCell className="font-medium">{sub.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-orange-200 text-orange-700">{sub.plan || "—"}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-green-700 font-medium">
                            {sub.discount_percent != null ? `${sub.discount_percent}%` : "—"}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "Forever"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={sub.is_active ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}
                          >
                            {sub.is_active ? "Active" : "Expired"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Pagination
                page={subsPage}
                totalPages={subsTotalPages}
                onPrev={() => setSubsPage((p) => p - 1)}
                onNext={() => setSubsPage((p) => p + 1)}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
